import { getCustomTrackedOffer } from '../core/modules/setupModelWidget/getFormattedOffers';
import { formatSingleOffer } from '../core/modules/setupModelWidget/getFormattedOffers/formatSingleOffer';
import type { Deal } from '../core/types/Deal';
import type { BuyingGuideBlockData } from '../core/types/HawkJSON';
import type { ModelExtra } from '../core/types/ModelInfo';
import type { TabletopBlockRow } from '../core/types/TabletopBlockRow';
import type { WidgetProps } from '../core/types/WidgetProps';
import {
  type ModelOffersResponseGetter,
  type ModelPhpGroupEntry,
  normalizeModelPhpGroups,
} from '../sharedModules/api/searchApi/createModelOffersResponseGetter';

import { getDealFromEditorialData } from './getDealFromEditorialData';

export type BuildTabletopBlocksInput = Pick<
  WidgetProps,
  | 'area'
  | 'articleName'
  | 'articleUrl'
  | 'articleType'
  | 'articleCategory'
  | 'trackingArea'
  | 'customPromo'
  | 'customPromos'
  | 'excludeFrom'
  | 'link'
  | 'linkText'
  | 'localiser'
  | 'params'
  | 'showPrice'
  | 'showPromos'
  | 'showWasPrice'
  | 'site'
  | 'widget'
  | 'social'
>;

export const apiRequestedModelNameForBlock = (block: BuyingGuideBlockData, index: number): string =>
  block.modelName || block.productName || `__tabletopblock__${index}`;

const allRequestedModelNamesForApiCall = (blocks: BuyingGuideBlockData[]): string[] =>
  blocks.map((block, index) => apiRequestedModelNameForBlock(block, index));

const getModelOffersBaseParams = (params: WidgetProps['params']): WidgetProps['params'] => {
  const baseParams = { ...params };
  delete baseParams.model_name;
  return baseParams;
};

const groupDisplayName = (group: ModelPhpGroupEntry, key: string): string =>
  group.requested_model?.trim() || group.widget.data.title.trim() || key;

const normalizeLookupName = (name: string): string => name.trim().toLowerCase();

const groupLookupNames = (group: ModelPhpGroupEntry, key: string): string[] => {
  const candidates = [group.requested_model, key, group.widget.data.title].filter(
    (name): name is string => Boolean(name?.trim()),
  );

  return Array.from(new Set(candidates.map(normalizeLookupName)));
};

const blockMatchesGroup = (
  block: BuyingGuideBlockData,
  blockIndex: number,
  group: ModelPhpGroupEntry,
  groupKey: string,
): boolean =>
  groupLookupNames(group, groupKey).includes(
    normalizeLookupName(apiRequestedModelNameForBlock(block, blockIndex)),
  );

const findEditorialBlockByRequestedModel = (
  blocks: BuyingGuideBlockData[],
  group: ModelPhpGroupEntry,
): BuyingGuideBlockData | undefined => {
  const requestedModel = group.requested_model?.trim();
  if (!requestedModel) {
    return undefined;
  }

  const matchedIndex = blocks.findIndex(
    (block, index) =>
      normalizeLookupName(apiRequestedModelNameForBlock(block, index)) ===
      normalizeLookupName(requestedModel),
  );

  return matchedIndex >= 0 ? blocks[matchedIndex] : undefined;
};

const findBlockIndexForGroup = (
  blocks: BuyingGuideBlockData[],
  group: ModelPhpGroupEntry,
  groupKey: string,
  usedBlockIndices: Set<number>,
): number | undefined => {
  const blockIndex = blocks.findIndex(
    (block, index) =>
      !usedBlockIndices.has(index) && blockMatchesGroup(block, index, group, groupKey),
  );

  return blockIndex === -1 ? undefined : blockIndex;
};

const displayModelNameForRow = (
  matchedBlock: BuyingGuideBlockData | undefined,
  groupKey: string,
  titleFromWidget: string,
): string => matchedBlock?.modelName || matchedBlock?.productName || titleFromWidget || groupKey;

const getFirstGroupModelInfo = (group: ModelPhpGroupEntry): ModelExtra | undefined => {
  const modelInfo = group.widget.data.model_info;
  if (!modelInfo) {
    return undefined;
  }
  return Object.values(modelInfo)[0];
};

const reviewFieldsFromModelInfo = (
  group: ModelPhpGroupEntry,
): { apiPros: BuyingGuideBlockData['pros']; apiStarRating: BuyingGuideBlockData['starRating'] } => {
  const modelInfo = getFirstGroupModelInfo(group);

  return {
    apiPros: modelInfo?.pros,
    apiStarRating:
      modelInfo?.score != null && modelInfo.score > 0 ? String(modelInfo.score * 10) : undefined,
  };
};

const withModelInfoReviewFallback = (
  block: BuyingGuideBlockData,
  group: ModelPhpGroupEntry,
): BuyingGuideBlockData => {
  const { apiPros, apiStarRating } = reviewFieldsFromModelInfo(group);

  return {
    ...block,
    pros: block.pros?.length ? block.pros : apiPros,
    starRating: block.starRating && Number(block.starRating) > 0 ? block.starRating : apiStarRating,
  };
};

const getGroupModelImageUrl = (group: ModelPhpGroupEntry): string | undefined => {
  const modelInfo = group.widget.data.model_info;
  if (!modelInfo) {
    return undefined;
  }

  for (const modelId of Object.values(group.models)) {
    const modelInfoEntry = modelInfo[modelId] as ModelExtra | undefined;
    if (modelInfoEntry?.model_image_url) {
      return modelInfoEntry.model_image_url;
    }
  }

  return undefined;
};

const buyingGuideBlockForTenancyGroup = (
  groupKey: string,
  group: ModelPhpGroupEntry,
  enrichmentBlock: BuyingGuideBlockData | undefined,
): BuyingGuideBlockData => {
  const displayName = groupDisplayName(group, groupKey);
  const { apiPros, apiStarRating } = reviewFieldsFromModelInfo(group);

  if (!enrichmentBlock) {
    return {
      modelName: displayName,
      productName: displayName,
      productHeader: 'Sponsored',
      pros: apiPros,
      starRating: apiStarRating,
    };
  }

  return {
    ...enrichmentBlock,
    modelName: enrichmentBlock.modelName ?? displayName,
    productName: enrichmentBlock.productName ?? displayName,
    productHeader: 'Sponsored',
    link: undefined,
    pros: apiPros ?? enrichmentBlock.pros,
    starRating: apiStarRating ?? enrichmentBlock.starRating,
  };
};

const buyingGuideBlockForGroup = (
  groupKey: string,
  group: ModelPhpGroupEntry,
  matchedBlock: BuyingGuideBlockData | undefined,
  tenancyEnrichmentBlock: BuyingGuideBlockData | undefined,
): BuyingGuideBlockData => {
  if (group.is_tenancy) {
    return buyingGuideBlockForTenancyGroup(groupKey, group, tenancyEnrichmentBlock);
  }

  if (matchedBlock !== undefined) {
    return withModelInfoReviewFallback(matchedBlock, group);
  }

  const displayName = groupDisplayName(group, groupKey);
  return withModelInfoReviewFallback({ modelName: displayName, productName: displayName }, group);
};

const getDealsForRow = (
  apiDeals: Deal[],
  editorialDeal: Deal | null,
  perRowCap: number,
): Deal[] => {
  if (editorialDeal) {
    const maxApi = Math.max(0, perRowCap - 1);
    const fromApi = apiDeals.slice(0, maxApi);
    const chosen: Deal[] = [...fromApi];
    if (chosen.length < perRowCap) {
      chosen.push(editorialDeal);
    }
    return chosen;
  }

  return apiDeals.slice(0, perRowCap);
};

const buildEditorialDealForBlock = async (
  widgetProps: BuildTabletopBlocksInput,
  block: BuyingGuideBlockData,
  blockIndex: number,
): Promise<Deal | null> => {
  if (!block.link?.url) {
    return null;
  }

  const editorialDeal = getDealFromEditorialData({
    block,
    index: blockIndex,
    localiser: widgetProps.localiser,
  });

  return getCustomTrackedOffer({
    deal: editorialDeal,
    site: widgetProps.site,
    articleUrl: widgetProps.articleUrl,
    area: widgetProps.area,
    widgetType: widgetProps.widget.type,
    articleName: widgetProps.articleName,
    articleType: widgetProps.articleType,
    articleCategory: widgetProps.articleCategory,
    social: widgetProps.social,
    trackingArea: widgetProps.trackingArea,
  });
};

const buildTabletopRowForApiGroup = async ({
  widgetProps,
  blocks,
  groupKey,
  group,
  matchedBlockIndex,
  dealsPerRowCap,
}: {
  widgetProps: BuildTabletopBlocksInput;
  blocks: BuyingGuideBlockData[];
  groupKey: string;
  group: ModelPhpGroupEntry;
  matchedBlockIndex: number | undefined;
  dealsPerRowCap: number;
}): Promise<TabletopBlockRow> => {
  const requestedModelNameForOffers =
    matchedBlockIndex === undefined
      ? groupDisplayName(group, groupKey)
      : apiRequestedModelNameForBlock(blocks[matchedBlockIndex], matchedBlockIndex);

  const localisedLinkText = widgetProps.linkText
    ? widgetProps.localiser.translate(widgetProps.linkText)
    : '';
  const offers = group.widget.data.offers ?? [];

  const formattedApiOffers = await Promise.all(
    offers.map((deal) =>
      formatSingleOffer({
        deal,
        localiser: widgetProps.localiser,
        localisedLinkText,
        showPrice: widgetProps.showPrice,
        showWasPrice: widgetProps.showWasPrice,
        showPromos: widgetProps.showPromos,
        customPromo: widgetProps.customPromo,
        customPromos: widgetProps.customPromos,
        excludeFrom: widgetProps.excludeFrom,
        site: widgetProps.site,
        area: widgetProps.area,
        articleName: widgetProps.articleName,
        articleUrl: widgetProps.articleUrl,
        articleType: widgetProps.articleType,
        articleCategory: widgetProps.articleCategory,
        trackingArea: widgetProps.trackingArea,
        params: { ...widgetProps.params, model_name: requestedModelNameForOffers },
        widget: widgetProps.widget,
        link: widgetProps.link,
        social: widgetProps.social,
      }),
    ),
  );

  const matchedBlock = matchedBlockIndex === undefined ? undefined : blocks[matchedBlockIndex];
  const tenancyEnrichmentBlock =
    group.is_tenancy && matchedBlock === undefined
      ? findEditorialBlockByRequestedModel(blocks, group)
      : undefined;

  const editorialDeal =
    matchedBlockIndex === undefined
      ? null
      : await buildEditorialDealForBlock(widgetProps, blocks[matchedBlockIndex], matchedBlockIndex);

  const deals = getDealsForRow(formattedApiOffers, editorialDeal, dealsPerRowCap);

  const buyingGuideBlock = buyingGuideBlockForGroup(
    groupKey,
    group,
    matchedBlock,
    tenancyEnrichmentBlock,
  );
  const groupModelImageUrl = getGroupModelImageUrl(group);
  const titleFromWidget = group.widget.data.title;
  const presentationBlock = matchedBlock ?? tenancyEnrichmentBlock;
  const modelName = displayModelNameForRow(presentationBlock, groupKey, titleFromWidget);

  return {
    buyingGuideBlock,
    modelName,
    deals,
    groupModelImageUrl,
  };
};

const buildTabletopRowForEditorialBlock = async ({
  widgetProps,
  block,
  blockIndex,
  dealsPerRowCap,
}: {
  widgetProps: BuildTabletopBlocksInput;
  block: BuyingGuideBlockData;
  blockIndex: number;
  dealsPerRowCap: number;
}): Promise<TabletopBlockRow> => {
  const editorialDeal = await buildEditorialDealForBlock(widgetProps, block, blockIndex);
  const deals = editorialDeal ? [editorialDeal].slice(0, dealsPerRowCap) : [];

  const requestedModelName = apiRequestedModelNameForBlock(block, blockIndex);
  const modelName = displayModelNameForRow(block, requestedModelName, '');

  return {
    buyingGuideBlock: block,
    modelName,
    deals,
  };
};

export const buildTabletopBlocks = async (
  widgetProps: BuildTabletopBlocksInput,
  blocks: BuyingGuideBlockData[],
  getModelOffersResponse: ModelOffersResponseGetter,
): Promise<TabletopBlockRow[]> => {
  if (blocks.length === 0) {
    return [];
  }

  const dealsPerRowCap = widgetProps.params.deals_per_model;

  const apiResponse = await getModelOffersResponse({
    ...getModelOffersBaseParams(widgetProps.params),
    model_names: allRequestedModelNamesForApiCall(blocks),
  });

  if (!apiResponse.ok) {
    return Promise.all(
      blocks.map((block, blockIndex) =>
        buildTabletopRowForEditorialBlock({ widgetProps, block, blockIndex, dealsPerRowCap }),
      ),
    );
  }

  const { groups } = apiResponse.data;
  const normalizedGroups = normalizeModelPhpGroups(groups);
  const usedBlockIndices = new Set<number>();
  const rows: TabletopBlockRow[] = [];

  for (const { key: groupKey, group } of normalizedGroups) {
    const matchedBlockIndex = group.is_tenancy
      ? undefined
      : findBlockIndexForGroup(blocks, group, groupKey, usedBlockIndices);

    if (matchedBlockIndex !== undefined) {
      usedBlockIndices.add(matchedBlockIndex);
    }

    rows.push(
      await buildTabletopRowForApiGroup({
        widgetProps,
        blocks,
        groupKey,
        group,
        matchedBlockIndex,
        dealsPerRowCap,
      }),
    );
  }

  return rows;
};
